import type { UiEvidence } from './types';

const SIGNAL_LABELS: Record<string, string> = {
  gender: 'Gender',
  country: 'Country',
  followerMagnitude: 'Follower scale',
  postMagnitude: 'Post volume',
  verified: 'Verified status',
  displayName: 'Display name',
  firstNameMismatch: 'First name',
  accountType: 'Account type',
  bioText: 'Bio text',
  language: 'Language',
  email: 'Email',
  website: 'Website',
  occupation: 'Occupation',
};

export function labelForSignal(name: string): string {
  return SIGNAL_LABELS[name] ?? name;
}

// Statuses other than IMAGE_ANALYSIS_COMPLETE mean no comparison actually
// ran (missing image, download failure, not attempted, not applicable) —
// verified against ImageEvidence.kt's resultClassification(), which only
// returns non-null when status === IMAGE_ANALYSIS_COMPLETE.
export function isImageUnavailable(status: string | null): boolean {
  return status !== 'IMAGE_ANALYSIS_COMPLETE';
}

export function describeMatchType(
  matchType: UiEvidence['matchType'],
  levenshteinDistance: number | null,
): string {
  switch (matchType) {
    case 'EXACT':
      return 'Exact handle match';
    case 'LEV1':
      return `Very close handle match${levenshteinDistance != null ? ` (${levenshteinDistance} character different)` : ''}`;
    case 'LEV2':
      return `Close handle match${levenshteinDistance != null ? ` (${levenshteinDistance} characters different)` : ''}`;
    case 'NOT_FOUND':
    default:
      return 'Handles do not closely match';
  }
}

function imageEvidenceSentence(evidence: UiEvidence): string | null {
  const { imageEvidence } = evidence;
  if (isImageUnavailable(imageEvidence.status)) {
    return null; // covered separately by the "image unavailable" state — not a scoring factor
  }
  switch (imageEvidence.decision) {
    case 'STRONG_MATCH':
      return 'Image evidence strongly supports this match.';
    case 'STRONG_MISMATCH':
      return 'Image evidence contradicts this match — the photos may show different people.';
    case 'INCONCLUSIVE':
    default:
      return 'Image evidence did not materially change the score.';
  }
}

/**
 * Builds a 1-2 sentence explanation entirely from the actual evaluation data
 * — never a hard-coded sentence. Sentence 1 covers handle + metadata signal
 * support/contradiction; sentence 2 (when applicable) covers image evidence.
 */
export function buildWhyExplanation(evidence: UiEvidence): string {
  const available = evidence.signals.filter(s => s.available);
  const supporting = available.filter(s => s.polarity === 'supports').map(s => labelForSignal(s.name).toLowerCase());
  const contradicting = available.filter(s => s.polarity === 'contradicts').map(s => labelForSignal(s.name).toLowerCase());

  const handlePhrase = describeMatchType(evidence.matchType, evidence.levenshteinDistance);

  let first: string;
  if (supporting.length > 0) {
    first = `${handlePhrase} plus matching ${joinList(supporting)} support this identity connection.`;
  } else if (contradicting.length > 0) {
    first = `${handlePhrase}, although ${joinList(contradicting)} ${contradicting.length > 1 ? 'differ' : 'differs'}.`;
  } else {
    first = `${handlePhrase}. No other metadata signals strongly support or contradict this match.`;
  }

  if (supporting.length > 0 && contradicting.length > 0) {
    first = `${handlePhrase} plus matching ${joinList(supporting)} support this identity connection, although ${joinList(contradicting)} ${contradicting.length > 1 ? 'differ' : 'differs'}.`;
  }

  const second = imageEvidenceSentence(evidence);
  return second ? `${first} ${second}` : first;
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
